import { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { Bold, Italic, Underline as UnderlineIcon, List as ListIcon, Type } from "lucide-react";

const PURIFY_CONFIG = {
  ALLOWED_TAGS: ["p", "br", "strong", "b", "em", "i", "u", "ul", "ol", "li", "a", "div", "span"],
  ALLOWED_ATTR: ["href", "target", "rel", "style"],
  ALLOW_DATA_ATTR: false,
};

export function RichEditor({ value, onChange, placeholder = "Write your cover letter…", "data-testid": testId }) {
  const ref = useRef(null);

  // Only sync value → DOM when it differs (e.g., initial load or external overwrite),
  // never on every keystroke so caret position is preserved.
  useEffect(() => {
    if (!ref.current) return;
    const clean = DOMPurify.sanitize(value || "", PURIFY_CONFIG);
    if (clean !== ref.current.innerHTML) {
      ref.current.innerHTML = clean;
    }
  }, [value]);

  const cmd = (c, arg) => {
    document.execCommand(c, false, arg);
    ref.current?.focus();
    const raw = ref.current?.innerHTML || "";
    onChange(DOMPurify.sanitize(raw, PURIFY_CONFIG));
  };

  const btn =
    "h-8 w-8 rounded-md hover:bg-[#E7F0EB] text-[#5C6661] hover:text-[#2D4A3E] flex items-center justify-center transition-colors";

  return (
    <div className="rounded-lg border border-[#E2E8E4] bg-white overflow-hidden focus-within:ring-2 focus-within:ring-[#2D4A3E]/20">
      <div className="flex items-center gap-0.5 border-b border-[#E2E8E4] bg-[#FAFBFA] px-2 py-1.5">
        <button type="button" onClick={() => cmd("bold")} className={btn} data-testid="rich-bold" aria-label="Bold">
          <Bold className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
        <button type="button" onClick={() => cmd("italic")} className={btn} data-testid="rich-italic" aria-label="Italic">
          <Italic className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => cmd("underline")} className={btn} data-testid="rich-underline" aria-label="Underline">
          <UnderlineIcon className="w-3.5 h-3.5" />
        </button>
        <div className="w-px h-4 bg-[#E2E8E4] mx-1.5" />
        <button type="button" onClick={() => cmd("insertUnorderedList")} className={btn} data-testid="rich-list" aria-label="Bullet list">
          <ListIcon className="w-3.5 h-3.5" />
        </button>
        <button type="button" onClick={() => cmd("formatBlock", "<p>")} className={btn} data-testid="rich-para" aria-label="New paragraph">
          <Type className="w-3.5 h-3.5" />
        </button>
        <div className="ml-auto text-[10px] uppercase tracking-widest text-[#9CA3AF] pr-1">Rich text</div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(DOMPurify.sanitize(e.currentTarget.innerHTML, PURIFY_CONFIG))}
        className="rich-editor min-h-[280px] max-h-[560px] overflow-y-auto p-4 text-sm text-[#121413] leading-relaxed focus:outline-none"
        data-testid={testId || "rich-editor"}
        data-placeholder={placeholder}
      />
    </div>
  );
}
